import { useState, useEffect, useCallback } from 'react';
import { Shield, Save, Loader2, AlertCircle, Lock, Clock, DollarSign, Eye, Key } from 'lucide-react';
import { bookingSecurityApi } from '../../api/bookingSecurityApi';
import type { BookingMode } from '../../api/bookingSecurityApi';
import { useSalonId } from '../../hooks/useSalonId';

const BookingSecurityConfigPanel = () => {
    const salonId = useSalonId();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Form state
    const [bookingMode, setBookingMode] = useState<BookingMode>('APPROVAL_REQUIRED');
    const [requirePhoneVerification, setRequirePhoneVerification] = useState(true);
    const [requireDeposit, setRequireDeposit] = useState(false);
    const [depositAmount, setDepositAmount] = useState<number>(0);
    const [maxBookingsPerHour, setMaxBookingsPerHour] = useState(3);
    const [maxBookingsPerDay, setMaxBookingsPerDay] = useState(10);
    const [showPricing, setShowPricing] = useState(true);
    const [showEmployeeFullNames, setShowEmployeeFullNames] = useState(true);
    const [bookingWindowDays, setBookingWindowDays] = useState(30);
    const [vipModeEnabled, setVipModeEnabled] = useState(false);
    const [vipAccessCode, setVipAccessCode] = useState('');

    const fetchConfig = useCallback(async () => {
        if (!salonId) return;
        try {
            setLoading(true);
            setError(null);
            const data = await bookingSecurityApi.getConfig(salonId);

            // Populate form
            setBookingMode(data.bookingMode);
            setRequirePhoneVerification(data.requirePhoneVerification);
            setRequireDeposit(data.requireDeposit);
            setDepositAmount(data.depositAmount || 0);
            setMaxBookingsPerHour(data.maxBookingsPerHour);
            setMaxBookingsPerDay(data.maxBookingsPerDay);
            setShowPricing(data.showPricing);
            setShowEmployeeFullNames(data.showEmployeeFullNames);
            setBookingWindowDays(data.bookingWindowDays);
            setVipModeEnabled(data.vipModeEnabled);
            setVipAccessCode(data.vipAccessCode || '');
        } catch (err) {
            console.error('Error fetching config:', err);
            setError('Error al cargar la configuración');
        } finally {
            setLoading(false);
        }
    }, [salonId]);

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    const handleSave = async () => {
        if (!salonId) return;
        try {
            setSaving(true);
            setError(null);
            setSuccess(false);

            await bookingSecurityApi.updateConfig(salonId, {
                bookingMode,
                requirePhoneVerification,
                requireDeposit,
                depositAmount: requireDeposit ? depositAmount : null,
                depositEnabledServices: null,
                maxBookingsPerHour,
                maxBookingsPerDay,
                showPricing,
                showEmployeeFullNames,
                bookingWindowDays,
                vipModeEnabled,
                vipAccessCode: vipModeEnabled ? vipAccessCode : null,
            });

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            console.error('Error saving config:', err);
            setError('Error al guardar la configuración');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg">
                    <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Seguridad de Reservas Públicas</h2>
                    <p className="text-gray-500">Configura la protección anti-spam y el modo de aprobación</p>
                </div>
            </div>

            {/* Error/Success Messages */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 flex items-center gap-2">
                    <Shield className="w-5 h-5 flex-shrink-0" />
                    <span>✓ Configuración guardada exitosamente</span>
                </div>
            )}

            {/* Booking Mode */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center gap-3">
                        <Lock className="w-5 h-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Modo de Reserva</h3>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid gap-3">
                        {/* OPEN Mode */}
                        <label className={`relative flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${bookingMode === 'OPEN'
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}>
                            <input
                                type="radio"
                                name="bookingMode"
                                value="OPEN"
                                checked={bookingMode === 'OPEN'}
                                onChange={() => {
                                    setBookingMode('OPEN');
                                    setVipModeEnabled(false);
                                }}
                                className="mt-1"
                            />
                            <div className="flex-1">
                                <div className="font-semibold text-gray-900">Abierto (Auto-confirmación)</div>
                                <p className="text-sm text-gray-500 mt-1">
                                    Las reservas se confirman automáticamente. Mayor conversión pero mayor riesgo.
                                </p>
                            </div>
                        </label>

                        {/* APPROVAL_REQUIRED Mode */}
                        <label className={`relative flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${bookingMode === 'APPROVAL_REQUIRED'
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}>
                            <input
                                type="radio"
                                name="bookingMode"
                                value="APPROVAL_REQUIRED"
                                checked={bookingMode === 'APPROVAL_REQUIRED'}
                                onChange={() => {
                                    setBookingMode('APPROVAL_REQUIRED');
                                    setVipModeEnabled(false);
                                }}
                                className="mt-1"
                            />
                            <div className="flex-1">
                                <div className="font-semibold text-gray-900">Aprobación Requerida (Recomendado)</div>
                                <p className="text-sm text-gray-500 mt-1">
                                    Revisas cada reserva antes de confirmar. Balance entre seguridad y UX.
                                </p>
                            </div>
                        </label>

                        {/* PRIVATE Mode */}
                        <label className={`relative flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${bookingMode === 'PRIVATE'
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}>
                            <input
                                type="radio"
                                name="bookingMode"
                                value="PRIVATE"
                                checked={bookingMode === 'PRIVATE'}
                                onChange={() => {
                                    setBookingMode('PRIVATE');
                                    setVipModeEnabled(true);
                                }}
                                className="mt-1"
                            />
                            <div className="flex-1">
                                <div className="font-semibold text-gray-900">Privado (Solo con Código VIP)</div>
                                <p className="text-sm text-gray-500 mt-1">
                                    Reservas solo para clientes con código de acceso. Máxima privacidad.
                                </p>
                            </div>
                        </label>
                    </div>
                </div>
            </section>

            {/* Security Verification */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Verificación de Seguridad</h3>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <label className="flex items-start gap-3">
                        <input
                            type="checkbox"
                            checked={requirePhoneVerification}
                            onChange={(e) => setRequirePhoneVerification(e.target.checked)}
                            className="mt-1"
                        />
                        <div>
                            <div className="font-medium text-gray-900">Requerir verificación de teléfono</div>
                            <p className="text-sm text-gray-500 mt-1">
                                Clientes deben verificar su número con código SMS antes de reservar
                            </p>
                        </div>
                    </label>
                </div>
            </section>

            {/* Anti-Spam Limits */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Límites Anti-Spam</h3>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Máximo de reservas por hora (por teléfono)
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="20"
                            value={maxBookingsPerHour}
                            onChange={(e) => setMaxBookingsPerHour(parseInt(e.target.value) || 3)}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Máximo de reservas por día (por teléfono)
                        </label>
                        <input
                            type="number"
                            min="1"
                            max="50"
                            value={maxBookingsPerDay}
                            onChange={(e) => setMaxBookingsPerDay(parseInt(e.target.value) || 10)}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        />
                    </div>
                </div>
            </section>

            {/* Deposit Settings */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center gap-3">
                        <DollarSign className="w-5 h-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Depósitos</h3>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <label className="flex items-start gap-3">
                        <input
                            type="checkbox"
                            checked={requireDeposit}
                            onChange={(e) => setRequireDeposit(e.target.checked)}
                            className="mt-1"
                        />
                        <div className="flex-1">
                            <div className="font-medium text-gray-900">Requerir depósito</div>
                            <p className="text-sm text-gray-500 mt-1">
                                Solicita un pago anticipado para confirmar la reserva
                            </p>
                        </div>
                    </label>

                    {requireDeposit && (
                        <div className="ml-7 pt-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Monto del depósito (COP)
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="1000"
                                value={depositAmount}
                                onChange={(e) => setDepositAmount(parseInt(e.target.value) || 0)}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                                placeholder="20000"
                            />
                        </div>
                    )}
                </div>
            </section>

            {/* Visibility Settings */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Visibilidad</h3>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <label className="flex items-start gap-3">
                        <input
                            type="checkbox"
                            checked={showPricing}
                            onChange={(e) => setShowPricing(e.target.checked)}
                            className="mt-1"
                        />
                        <div>
                            <div className="font-medium text-gray-900">Mostrar precios</div>
                            <p className="text-sm text-gray-500 mt-1">
                                Los clientes verán el precio de cada servicio
                            </p>
                        </div>
                    </label>

                    <label className="flex items-start gap-3">
                        <input
                            type="checkbox"
                            checked={showEmployeeFullNames}
                            onChange={(e) => setShowEmployeeFullNames(e.target.checked)}
                            className="mt-1"
                        />
                        <div>
                            <div className="font-medium text-gray-900">Mostrar nombres completos de empleados</div>
                            <p className="text-sm text-gray-500 mt-1">
                                Si está desactivado, solo se mostrarán iniciales
                            </p>
                        </div>
                    </label>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Ventana de reserva (días hacia el futuro)
                        </label>
                        <input
                            type="number"
                            min="7"
                            max="90"
                            value={bookingWindowDays}
                            onChange={(e) => setBookingWindowDays(parseInt(e.target.value) || 30)}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                        />
                        <p className="text-sm text-gray-500 mt-2">
                            Los clientes podrán reservar hasta {bookingWindowDays} días en el futuro
                        </p>
                    </div>
                </div>
            </section>

            {/* VIP Mode */}
            <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center gap-3">
                        <Key className="w-5 h-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Modo VIP</h3>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <label className="flex items-start gap-3">
                        <input
                            type="checkbox"
                            checked={vipModeEnabled}
                            onChange={(e) => {
                                const enabled = e.target.checked;
                                setVipModeEnabled(enabled);
                                if (enabled) {
                                    setBookingMode('PRIVATE');
                                } else if (bookingMode === 'PRIVATE') {
                                    setBookingMode('APPROVAL_REQUIRED');
                                }
                            }}
                            className="mt-1"
                        />
                        <div className="flex-1">
                            <div className="font-medium text-gray-900">Activar modo VIP</div>
                            <p className="text-sm text-gray-500 mt-1">
                                Solo clientes con código de acceso podrán reservar
                            </p>
                        </div>
                    </label>

                    {vipModeEnabled && (
                        <div className="ml-7 pt-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Código VIP
                            </label>
                            <input
                                type="text"
                                value={vipAccessCode}
                                onChange={(e) => setVipAccessCode(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all font-mono"
                                placeholder="MI-CODIGO-SECRETO"
                            />
                            <p className="text-sm text-gray-500 mt-2">
                                Comparte este código solo con tus clientes VIP
                            </p>
                        </div>
                    )}
                </div>
            </section>

            {/* Save Button */}
            <div className="flex justify-end pb-8">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {saving ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Guardando...
                        </>
                    ) : (
                        <>
                            <Save className="w-5 h-5" />
                            Guardar Configuración
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default BookingSecurityConfigPanel;
